import { useEffect, useState, useMemo } from "react";
import { subscribe } from "../../lib/hyperliquid";
import { formatPrice } from "../../lib/format";

export default function OrderBook({ coin }) {
    const [book, setBook] = useState({ asks: [], bids: [] });

    useEffect(() => {
        if (!coin) return;
        setBook({ asks: [], bids: [] });
        const unsub = subscribe({ type: "l2Book", coin }, (data) => {
            const levels = data.levels || [[], []];
            const bids = levels[0] || [];
            const asks = levels[1] || [];
            setBook({
                bids: bids.slice(0, 10).map((l) => ({ px: +l.px, sz: +l.sz, n: +l.n })),
                asks: asks.slice(0, 10).map((l) => ({ px: +l.px, sz: +l.sz, n: +l.n })),
            });
        });
        return unsub;
    }, [coin]);

    const maxSz = useMemo(() => {
        const all = [...book.asks, ...book.bids].map((l) => l.sz);
        return all.length ? Math.max(...all) : 1;
    }, [book]);

    const bestBid = book.bids[0]?.px;
    const bestAsk = book.asks[0]?.px;
    const mid = bestBid && bestAsk ? (bestBid + bestAsk) / 2 : null;
    const spread = bestBid && bestAsk ? bestAsk - bestBid : null;

    const row = (l, isAsk) => {
        const pct = (l.sz / maxSz) * 100;
        return (
            <div key={`${isAsk ? "a" : "b"}-${l.px}`} className="relative grid grid-cols-3 items-center px-2 py-[3px] text-[11px] font-mono">
                <div
                    className={`absolute inset-y-0 right-0 ${isAsk ? "bg-rose-500/10" : "bg-emerald-500/10"}`}
                    style={{ width: `${pct}%` }}
                />
                <span className={`relative ${isAsk ? "text-rose-400" : "text-emerald-400"}`}>{formatPrice(l.px)}</span>
                <span className="relative text-right text-zinc-300">{l.sz.toFixed(4)}</span>
                <span className="relative text-right text-zinc-500">{l.n}</span>
            </div>
        );
    };

    return (
        <div className="bg-[#0A0A0C] border border-[#1F1F24] rounded-sm flex flex-col" data-testid="orderbook-panel">
            <div className="px-4 py-3 border-b border-[#1F1F24]">
                <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">Order Book</div>
                <div className="text-sm font-medium font-mono tracking-tight mt-0.5">L2 · {coin}</div>
            </div>
            <div className="grid grid-cols-3 px-2 py-1.5 text-[9px] font-mono uppercase tracking-[0.18em] text-zinc-500 border-b border-[#1F1F24]">
                <span>Price</span>
                <span className="text-right">Size</span>
                <span className="text-right">Orders</span>
            </div>
            <div className="px-1 py-1">
                {book.asks.slice().reverse().map((l) => row(l, true))}
            </div>
            <div className="px-3 py-2 border-y border-[#1F1F24] bg-black/40 flex justify-between items-baseline">
                <div>
                    <div className="text-[9px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Mid</div>
                    <div className="font-mono text-base text-cyan-400 mt-0.5">{mid ? formatPrice(mid) : "—"}</div>
                </div>
                <div className="text-right">
                    <div className="text-[9px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Spread</div>
                    <div className="font-mono text-xs text-zinc-300 mt-0.5">{spread != null ? formatPrice(spread) : "—"}</div>
                </div>
            </div>
            <div className="px-1 py-1">
                {book.bids.map((l) => row(l, false))}
            </div>
        </div>
    );
}
